<template>
  <div>
    <!-- Table -->
    <b-table
      id="usersTable"
      v-if="getAvailableList"
      :fields="fields"
      :items="getUserList"
      striped
      bordered
      responsive
      hover
      stacked="lg"
      show-empty
      small
      class="users-table"
      :tbody-tr-class="rowClass"
    >
      <!-- Habilitado: switch para deshabilitar o volver a habilitar -->
      <template #cell(is_disabled)="row">
        <b-form-checkbox
          v-if="canToggle(row.item)"
          :key="`switch-${row.item.id}-${switchKey}`"
          switch
          :checked="!row.item.is_disabled"
          :disabled="toggling === row.item.id"
          :title="row.item.is_disabled ? 'Habilitar' : 'Deshabilitar'"
          @change="toggleUser(row.item)"
        />
        <!-- Sin permiso, o en tu propia fila: el switch solo se muestra, no se puede cambiar -->
        <span
          v-else
          class="d-inline-block"
          :title="isAuthUserOrCloneUser(row.item.id) ? 'No puedes deshabilitar tu propia cuenta' : ''"
        >
          <b-form-checkbox switch disabled :checked="!row.item.is_disabled" />
        </span>
      </template>

      <!-- Último acceso -->
      <template #cell(ultimo_acceso)="row">
        <span
          v-if="row.item.ultimo_acceso"
          :title="formatAccess(row.item.ultimo_acceso)"
          :class="{ 'text-muted': row.item.is_disabled, 'text-warning': isStale(row.item) }"
        >
          {{ fromNow(row.item.ultimo_acceso) }}
        </span>
        <span v-else class="text-muted">Nunca</span>
      </template>

      <!-- Nombre -->
      <template #cell(persona.nombre)="row">
        <span :class="{ 'text-muted': row.item.is_disabled }">{{ row.item.persona.nombre }}</span>
      </template>

      <!-- Email -->
      <template #cell(email)="row">
        <span :class="{ 'text-muted': row.item.is_disabled }">{{ row.item.email }}</span>
      </template>

      <!-- Profesión -->
      <template #cell(empleado.profesion)="row">
        <span :class="{ 'text-muted': row.item.is_disabled }">
          {{ row.item.empleado.profesion }}
        </span>
      </template>

      <!-- Fecha de ingreso -->
      <template #cell(empleado.fecha_ingreso)="row">
        <span :class="{ 'text-muted': row.item.is_disabled }">
          {{ row.item.empleado.fecha_ingreso }}
        </span>
      </template>

      <!-- Roles -->
      <template #cell(roles_checklist)="data">
        <b-badge
          v-for="role in data.item.roles_checklist"
          :key="role.id"
          :variant="getRoleVariant(role.name)"
          class="mr-1"
        >
          {{ role.name }}
        </b-badge>
        <span v-if="data.item.roles_checklist.length <= 0" class="text-danger">
          Sin roles asignados
        </span>
      </template>

      <!-- Acciones -->
      <template #cell(acciones)="row">
        <div class="d-flex align-items-center user-actions">
          <b-button size="sm" variant="light" class="btn-outline-black" @click="openDetail(row.item)">
            Detalle
          </b-button>

          <!-- El botón de "ver como", únicamente lo puede usar el usuario con rol "admin" -->
          <check-authorization
            :requiresRoles="['admin']"
            :show-alert="false"
            hide
            v-if="!row.item.is_disabled && !isAuthUserOrCloneUser(row.item.id)"
          >
            <b-button size="sm" variant="light" class="btn-outline-indigo" @click="viewAsUser(row.item.id)">
              Ver como
            </b-button>
          </check-authorization>

          <!-- Eliminar lo quita de la lista; no se ofrece en tu propia fila -->
          <u-d-controls
            :id="row.item.id"
            editPath="userModule/getUserToEdit"
            deletePath="userModule/deleteUser"
            :editPermissions="['editar usuarios']"
            :deletePermissions="['eliminar usuarios']"
            :hideDeleteAction="isAuthUserOrCloneUser(row.item.id)"
            @on-got-item="emitOnGotItem"
          ></u-d-controls>
        </div>
      </template>
    </b-table>
    <loading v-else></loading>

    <!-- Detalle en panel lateral -->
    <user-detail :user="selectedUser" :loading="loadingDetail" @close="closeDetail" />
    <div v-if="isThereUsers">
      <paginator
        aria-controls="usersTable"
        :disabled="!getAvailableList"
        show-path="userModule/getUsers"
        per-page-path="userModule/changePerPage"
        :resource="getUsersResource"
      ></paginator>
    </div>
    <!-- End Table -->
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import UserDetail from '@/components/Users/UserDetail'

// Sin entrar en este tiempo, el último acceso se resalta.
const STALE_DAYS = 90

export default {
  name: 'UserList',

  components: {
    UserDetail
  },

  created () {
    this.submit()
  },

  data () {
    return {
      selectedUser: null,

      loadingDetail: false,

      // Id del usuario que se está habilitando o deshabilitando.
      toggling: null,

      // Al cancelar se cambia para que el switch vuelva a su posición.
      switchKey: 0,

      fields: [
        { key: 'is_disabled', label: 'Habilitado', sortable: true },
        { key: 'persona.nombre', label: 'Nombre', sortable: true },
        { key: 'email', label: 'Email' },
        { key: 'empleado.profesion', label: 'Profesión' },
        { key: 'empleado.fecha_ingreso', label: 'Fecha de ingreso', sortable: true },
        { key: 'roles_checklist', label: 'Roles' },
        { key: 'ultimo_acceso', label: 'Último acceso' },
        // Ancho mínimo: los botones no se quedan flotando a la derecha del espacio sobrante.
        { key: 'acciones', label: '', class: 'users-table__actions' }
      ]
    }
  },

  computed: {
    ...mapGetters('userModule', ['getAuthPermissions']),

    ...mapGetters('userModule', [
      'getUserList',
      'isAuthUserOrCloneUser',
      'getAvailableList',
      'isThereUsers',
      'getUsersResource'
    ])
  },

  methods: {
    async submit () {
      const { error, message } = await this.$store.dispatch('userModule/getUsers')

      if (error) this.$notify({ error, message }, 'Usuarios')
    },

    /**
     * Abre el panel con lo que ya trae la fila y pide el resto
     * (permisos, adscripción y último acceso) al servidor.
     */
    async openDetail (item) {
      this.selectedUser = item
      this.loadingDetail = true

      const { error, message, data } = await this.$store.dispatch('userModule/getUserDetail', item.id)

      // Si se cerró o se abrió otro usuario mientras cargaba, no se toca.
      if (!this.selectedUser || this.selectedUser.id !== item.id) return

      this.loadingDetail = false

      if (error) return this.$notify({ error, message }, 'Detalle de usuario')

      this.selectedUser = data
    },

    closeDetail () {
      this.selectedUser = null
      this.loadingDetail = false
    },

    canToggle (item) {
      return this.getAuthPermissions.includes('editar usuarios') && !this.isAuthUserOrCloneUser(item.id)
    },

    async toggleUser (item) {
      const nombre = item.persona ? `${item.persona.nombre} ${item.persona.primer_apellido}` : item.email
      const enabling = item.is_disabled
      const ok = await this.$bvModal.msgBoxConfirm(
        enabling
          ? `${nombre} podrá volver a entrar al sistema con sus roles anteriores.`
          : `${nombre} ya no podrá entrar al sistema, pero seguirá en la lista. Puedes volver a habilitarlo cuando quieras.`,
        {
          title: enabling ? '¿Habilitar usuario?' : '¿Deshabilitar usuario?',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: enabling ? 'success' : 'danger',
          okTitle: enabling ? 'Habilitar' : 'Deshabilitar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          centered: true
        }
      )

      if (!ok) {
        this.switchKey++
        return
      }

      this.toggling = item.id
      const { error, message } = await this.$store.dispatch('userModule/setUserEnabled', {
        id: item.id,
        enabled: enabling
      })
      this.toggling = null

      this.$notify({ error, message }, 'Usuarios')
    },

    fromNow (date) {
      return moment(date).fromNow()
    },

    formatAccess (date) {
      return moment(date).format('DD [de] MMMM [de] YYYY, h:mm a')
    },

    isStale (item) {
      return !item.is_disabled && moment().diff(moment(item.ultimo_acceso), 'days') > STALE_DAYS
    },

    getRoleVariant (role) {
      if (role === 'admin') return 'success'

      return 'primary'
    },

    async viewAsUser (id) {
      await this.$store.dispatch('userModule/getViewAsUser', id)
      this.$router.push({ name: 'Dashboard' })
      window.scrollTo(0, 0)
    },

    rowClass (item) {
      return item && item.is_disabled ? 'users-table__row--disabled' : ''
    },

    emitOnGotItem () {
      this.$emit('on-got-item')
    }
  }
}
</script>

<style lang="scss" scoped>
.users-table ::v-deep td {
  vertical-align: middle;
  font-size: 0.8rem;
}

.users-table ::v-deep .users-table__row--disabled td {
  background: #fafbfc;
}

.users-table ::v-deep .users-table__actions {
  width: 1%;
  white-space: nowrap;
}

.user-actions {
  gap: 0.35rem;
}

.user-actions ::v-deep .btn {
  white-space: nowrap;
}

.user-actions ::v-deep .mr-3 {
  margin-right: 0.35rem !important;
}
</style>
